/**
 * Live Search functionality.
 *
 * @module feature-navigation/search
 */
import { debounce } from "@valtech/utilities";

/* ----------------------------------------
 * Required Selectors / Classnames / IDs
 * ---------------------------------------- */
var idNames = {
  desktop: {
    input: "main-nav-search",
    suggestions: "main-nav-search-suggestions",
  },
  mobile: {
    input: "main-nav-search-mob",
    suggestions: "main-nav-search-suggestions-mob",
  },
};

/* ----------------------------------------
 * DOM Elements
 * ---------------------------------------- */
function getElements() {
  return {
    desktop: {
      input: document.getElementById(idNames.desktop.input),
      suggestions: document.getElementById(idNames.desktop.suggestions),
    },
    mobile: {
      input: document.getElementById(idNames.mobile.input),
      suggestions: document.getElementById(idNames.mobile.suggestions),
    },
  };
}

/* ----------------------------------------
 * Search Config
 * ---------------------------------------- */
function getSearchConfig() {
  var root = document.querySelector("nav.main-navigation");

  return {
    apiUrl:
      root && root.dataset && root.dataset.searchApi
        ? root.dataset.searchApi
        : "/api/search",

    useMock: root && root.dataset && root.dataset.searchUseMock === "true",

    mockData:
      root && root.dataset && root.dataset.searchMock
        ? JSON.parse(root.dataset.searchMock)
        : [],

    searchdelay:
      root && root.dataset && root.dataset.searchDelay
        ? JSON.parse(root.dataset.searchDelay)
        : 250,
  };
}

/* ----------------------------------------
 * API Fetch
 * ---------------------------------------- */
function fetchSuggestions(config, query) {
  if (!query || query.length < 3) {
    return Promise.resolve([]);
  }

  // using mock mode
  if (config.useMock) {
    return Promise.resolve(config.mockData);
  }

  // Real API call
  var url = config.apiUrl + "?q=" + encodeURIComponent(query);

  return fetch(url)
    .then(function (res) {
      if (!res.ok) throw new Error("Fetch failed");
      return res.json();
    })
    .then(function (data) {
      return data.results || data;
    })
    .catch(function (err) {
      console.error("[Search] Error fetching suggestions:", err);
      return [];
    });
}

/* ----------------------------------------
 * Render Suggestions (Safe & Linked)
 * ---------------------------------------- */
function renderSuggestions(container, items) {
  if (!items || items.length === 0) {
    container.style.display = "none";
    return;
  }

  container.innerHTML = "";

  var ul = document.createElement("ul");

  items.forEach(function (item) {
    var li = document.createElement("li");
    var a = document.createElement("a");

    a.href = item.value;

    // Use textContent instead of innerHTML to prevent malicious scripts from executing
    a.textContent = item.label;

    li.appendChild(a);
    ul.appendChild(li);
  });

  container.appendChild(ul);
  container.style.display = "block";
}

/* ----------------------------------------
 * Attach event handlers to an input
 * ---------------------------------------- */
function attachLiveSearch(input, container, config) {
  if (!input || !container) return;

  var handler = debounce(function () {
    var value = input.value.trim();

    if (value.length < 4) {
      container.style.display = "none";
      return;
    }

    fetchSuggestions(config, value).then(function (suggestions) {
      renderSuggestions(container, suggestions, input);
    });
  }, config.searchdelay);

  input.addEventListener("input", handler);

  document.addEventListener("click", function (e) {
    if (!container.contains(e.target) && e.target !== input) {
      container.style.display = "none";
    }
  });
}

/* ----------------------------------------
 * Init function (exported)
 * ---------------------------------------- */
export default function initLiveSearch() {
  var config = getSearchConfig();
  var elements = getElements();

  // Desktop search
  attachLiveSearch(
    elements.desktop.input,
    elements.desktop.suggestions,
    config
  );

  // Mobile search
  attachLiveSearch(elements.mobile.input, elements.mobile.suggestions, config);
}
